// Minimal route-level error boundary.
//
// Two failure modes are handled distinctly:
//
//   1. ChunkLoadError — the user has an old `index.html` cached that
//      points at a JS chunk name that no longer exists on the CDN
//      (typical after a redeploy). React 18 unmounts the failed route
//      silently → operator sees a "black screen" with no console error
//      visible in normal usage. We catch it and render a clear
//      "New version available — reload page" panel with a 1-click
//      reload button.
//
//   2. Any other render-time exception — render a small "Something
//      went wrong on this page" card with the error message + reset
//      button (does not crash the whole SPA, only the current route).
//
// The boundary intentionally has NO router/store dependencies so it
// can be safely wrapped around every top-level <Route element=...> in
// `App.js`. Keep it tiny.
import { Component } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";

function isChunkLoadError(err) {
  if (!err) return false;
  const name = err?.name || "";
  const msg = String(err?.message || err);
  return (
    name === "ChunkLoadError" ||
    /Loading chunk \S+ failed/i.test(msg) ||
    /Loading CSS chunk \S+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep console trace useful for ops even though the fallback UI
    // is rendered. Stack already includes the offending component.
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary] caught", error, info?.componentStack);
  }

  handleReload = () => {
    try {
      // Force a network reload bypassing browser cache so the new
      // `index.html` (and the new chunk-name manifest) is fetched.
      window.location.reload(true);
    } catch {
      window.location.reload();
    }
  };

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (isChunkLoadError(error)) {
      return (
        <div
          className="min-h-[60vh] flex items-center justify-center px-6"
          data-testid="route-chunk-error"
        >
          <div className="max-w-md bg-[#0F0F14] border border-border rounded-sm p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <RefreshCcw size={28} className="text-[#A0A0AB]" />
            </div>
            <div className="text-base text-[#E4E4E7] mb-2">
              New version available
            </div>
            <div className="text-sm text-[#A0A0AB] mb-5 font-mono">
              A newer build of the bot has been deployed. Reload to
              load the latest assets.
            </div>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="route-chunk-error-reload"
              className="bg-[#00E676] text-[#0A0A0F] font-mono px-4 py-2 rounded-sm hover:opacity-90"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className="min-h-[60vh] flex items-center justify-center px-6"
        data-testid="route-render-error"
      >
        <div className="max-w-lg bg-[#0F0F14] border border-[#FF5252]/30 rounded-sm p-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={22} className="text-[#FF5252]" />
            <div className="text-base text-[#E4E4E7]">
              Something went wrong on this page
            </div>
          </div>
          <div className="text-xs text-[#FF5252] mb-4 font-mono break-words">
            {String(error?.message || error)}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              data-testid="route-render-error-retry"
              className="bg-[#0F0F14] border border-border text-[#E4E4E7] font-mono px-3 py-1.5 rounded-sm hover:bg-[#13131A]"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="route-render-error-reload"
              className="text-xs text-[#71717A] hover:text-[#E4E4E7]"
            >
              or reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
